import { useAuth } from "@/modules/auth";
import { WallOfLoveList, WallOfLoveListSkeleton, useWallOfLoves, type WallOfLove } from "@/modules/wall-of-love";
import WallOfLoveEmptyPlaceholder from "@/modules/wall-of-love/components/wall-of-love-empty-placeholder";
import StatusChangeConfirmationModal from "@/modules/wall-of-love/components/status-change-confirmation-modal";
import PublishedLinkModal from "@/modules/wall-of-love/components/published-link-modal";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import Container from "@/components/ui/container";
import Button from "@/components/ui/button";
import { CreateWallOfLoveModal } from "@/modules/wall-of-love";
import { DeleteConfirmationModal } from "@/components/ui/delete-confirmation-modal";
import { useToast } from "@/hooks/use-toast";
import { useOnboardingStore } from "@/modules/onboarding/stores/onboarding-store";
import { useBrands } from "@/modules/brand";

export default function WallOfLovePage() {
	const { wallOfLoves, indexWallOfLove, updateWallOfLove, deleteWallOfLove, createWallOfLove } = useWallOfLoves();
	const { currentWorkspaceId } = useAuth();
	const { brandUrl } = useParams();
	const { selectedBrand, viewBrand } = useBrands();
	const [loading, setLoading] = useState(false);
	const [createModalOpen, setCreateModalOpen] = useState(false);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [selectedWallOfLove, setSelectedWallOfLove] = useState<WallOfLove | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);
	const [statusChangeModalOpen, setStatusChangeModalOpen] = useState(false);
	const [pendingStatusChange, setPendingStatusChange] = useState<{ id: string; currentStatus: "published" | "unpublished"; newStatus: "published" | "unpublished" } | null>(null);
	const [isStatusChanging, setIsStatusChanging] = useState(false);
	const [publishedLinkModalOpen, setPublishedLinkModalOpen] = useState(false);
	const [publishedWallInfo, setPublishedWallInfo] = useState<{ title: string; link: string } | null>(null);
	const navigate = useNavigate();
	const { showToast } = useToast();

	useEffect(() => {
		if (!currentWorkspaceId) return;

		(async () => {
			setLoading(true);
			const result = await indexWallOfLove(currentWorkspaceId);
			setLoading(false);
			if (result?.data && result.data.length > 0) {
				const store = useOnboardingStore.getState();
				if (!store.completedSteps.includes("setup_wall_of_love")) {
					store.completeStep(currentWorkspaceId, "setup_wall_of_love");
				}
			}
		})();
	}, [currentWorkspaceId, indexWallOfLove]);

	// Load brand data to get user role
	useEffect(() => {
		if (currentWorkspaceId) {
			viewBrand(currentWorkspaceId);
		}
	}, [currentWorkspaceId, viewBrand]);

	const handleCreateWallOfLove = () => {
		setCreateModalOpen(true);
	};

	const handleCreateWallOfLoveSubmit = async (data: { title: string }): Promise<{ success: boolean; data?: { id: string } }> => {
		if (!currentWorkspaceId) {
			return {
				success: false,
			};
		}

		const result = await createWallOfLove(currentWorkspaceId, { title: data.title });

		if (result.success && result.data) {
			useOnboardingStore.getState().completeStep(currentWorkspaceId, "setup_wall_of_love");
			navigate(`/${currentWorkspaceId}/wall-of-love/${result.data.id}/edit`);
			return {
				success: true,
				data: { id: result.data.id },
			};
		} else {
			showToast({
				title: "Failed to create wall of love",
				description: "Something went wrong. Please try again.",
				status: "error",
				duration: 5000,
			});
			return {
				success: false,
			};
		}
	};

	const handleCreateWallOfLoveSuccess = () => {
		// Navigation handled in handleCreateWallOfLoveSubmit
		// This callback is called after successful creation
	};

	const handleEdit = (id: string) => {
		navigate(`/${currentWorkspaceId}/wall-of-love/${id}/edit`);
	};

	const handleDelete = async (id: string) => {
		const wallOfLove = wallOfLoves?.find((wol) => wol.id === id) || null;
		setSelectedWallOfLove(wallOfLove);
		setDeleteModalOpen(true);
	};

	const handleDeleteConfirm = async (): Promise<void> => {
		if (!currentWorkspaceId) return;
		if (selectedWallOfLove === null) return;

		setIsDeleting(true);
		const result = await deleteWallOfLove(selectedWallOfLove.id, currentWorkspaceId);
		setIsDeleting(false);
		setDeleteModalOpen(false);

		if (result.success) {
			showToast({
				title: "Wall of Love deleted",
				description: "The wall of love has been deleted successfully.",
				status: "success",
				duration: 5000,
			});
			await indexWallOfLove(currentWorkspaceId);
		} else {
			showToast({
				title: "Failed to delete wall of love",
				description: "Something went wrong. Please try again.",
				status: "error",
				duration: 5000,
			});
		}
	};

	const handleStatusChange = async (id: string, status: string) => {
		if (!currentWorkspaceId) return;

		const wallOfLove = wallOfLoves?.find((wol) => wol.id === id);
		if (!wallOfLove) return;

		const currentStatus = wallOfLove.status as "published" | "unpublished";
		const newStatus = status as "published" | "unpublished";

		// Don't show confirmation if status is not changing
		if (currentStatus === newStatus) return;

		// Show confirmation modal for status changes
		setPendingStatusChange({ id, currentStatus, newStatus });
		setStatusChangeModalOpen(true);
	};

	const handleStatusChangeConfirm = async () => {
		if (!currentWorkspaceId || !pendingStatusChange) return;

		const isPublishing = pendingStatusChange.newStatus === "published";
		const wallOfLove = wallOfLoves?.find((wol) => wol.id === pendingStatusChange.id);

		setIsStatusChanging(true);
		const result = await updateWallOfLove(pendingStatusChange.id, currentWorkspaceId, { status: pendingStatusChange.newStatus });
		setIsStatusChanging(false);

		if (result.success) {
			await indexWallOfLove(currentWorkspaceId);

			if (isPublishing && wallOfLove) {
				// Show published link modal instead of toast
				const wallLink = `${import.meta.env.VITE_CLIENT_WALLS_URL}/w/${wallOfLove.slug}`;
				setPublishedWallInfo({ title: wallOfLove.title || "Untitled", link: wallLink });
				setPublishedLinkModalOpen(true);
			} else {
				showToast({
					title: "Status updated",
					description: "The wall of love status has been updated.",
					status: "success",
					duration: 5000,
				});
			}
		} else {
			showToast({
				title: "Failed to update status",
				description: "Something went wrong. Please try again.",
				status: "error",
				duration: 5000,
			});
		}

		setStatusChangeModalOpen(false);
		setPendingStatusChange(null);
	};

	const hasWallOfLoves = wallOfLoves && wallOfLoves.length > 0;
	const hasNoWallOfLoves = !loading && wallOfLoves !== null && wallOfLoves.length === 0;

	return (
		<>
			{hasNoWallOfLoves ? (
				<div className="py-12">
					<Container>
						<div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
							<h2 className="text-xl font-semibold text-primary mb-2">No wall of love yet</h2>
							<p className="text-tertiary text-center max-w-md mb-8">
								Effortlessly collect, organize, and showcase customer testimonials with your Wall of Love. Make it easy to highlight positive feedback and build trust with your
								audience.
							</p>
							<WallOfLoveEmptyPlaceholder onClick={handleCreateWallOfLove} />
						</div>
					</Container>
				</div>
			) : (
				<div className="space-y-6 py-12">
					<Container>
						{hasWallOfLoves && (
							<div className="flex items-center justify-between mb-6">
								<div>
									<h1 className="text-xl font-semibold text-primary">Wall of Love</h1>
									<p className="mt-1 text-tertiary max-w-[640px]">
										Effortlessly collect, organize, and showcase customer testimonials with your Wall of Love. Make it easy to highlight positive feedback and build trust with your
										audience.
									</p>
								</div>
								<div>
									<Button className="block" type="button" onClick={handleCreateWallOfLove}>
										Create Wall of Love
									</Button>
								</div>
							</div>
						)}

						<div>
						{loading && <WallOfLoveListSkeleton />}
						{hasWallOfLoves && !loading && <WallOfLoveList wallOfLoves={wallOfLoves} onEdit={handleEdit} onDelete={handleDelete} onStatusChange={handleStatusChange} brandUrl={brandUrl} userRole={selectedBrand?.role} />}
						</div>
					</Container>
				</div>
			)}

			<CreateWallOfLoveModal open={createModalOpen} onOpenChange={setCreateModalOpen} onSuccess={handleCreateWallOfLoveSuccess} onSubmit={handleCreateWallOfLoveSubmit} />

			<DeleteConfirmationModal
				open={deleteModalOpen}
				title="Delete Wall of Love"
				description={`Are you sure you want to delete the wall of love "${selectedWallOfLove?.title}"? This action cannot be undone.`}
				isLoading={isDeleting}
				onOpenChange={setDeleteModalOpen}
				onConfirm={handleDeleteConfirm}
			/>

			<StatusChangeConfirmationModal
				open={statusChangeModalOpen}
				onOpenChange={setStatusChangeModalOpen}
				currentStatus={pendingStatusChange?.currentStatus || "unpublished"}
				newStatus={pendingStatusChange?.newStatus || "unpublished"}
				onConfirm={handleStatusChangeConfirm}
				isLoading={isStatusChanging}
			/>

			<PublishedLinkModal
				open={publishedLinkModalOpen}
				onOpenChange={setPublishedLinkModalOpen}
				wallTitle={publishedWallInfo?.title || ""}
				link={publishedWallInfo?.link || ""}
			/>
		</>
	);
}
